import styles from "./ContactEdit.module.css";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Button, Checkbox, TextInput } from "@wwg/ui";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import mobile from "shared/mobile";
import { IconContact } from "@wwg/icons";
import { CONTACT_TYPE } from "shared/enums";
import { hasBitwise } from "shared/utils/hasBitwise";
import { useMerge } from "shared/hooks/useMerge";
import { Modal } from "../Modal/Modal";
import { ModalContent } from "../Modal/Modal";
import { ModalControls } from "../Modal/Modal";

/**
 * Form for editing a contact. Takes a `contact` data object,
 */
export function ContactEdit({ contact, onCancel, onSave, onArchive }) {
  const { t } = useTranslation();
  const [_contact, _mergeContact] = useMerge(contact);

  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const _onSubmit = useCallback((e) => {
    e.preventDefault();
  }, []);

  const _onFind = useCallback(() => {
    mobile
      .selectContact()
      .then((contact) => {
        _mergeContact(contact);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [_mergeContact]);

  const _onSave = useCallback(
    (e) => {
      e.preventDefault();

      var formIsValid = true;
      var errors = {};

      if (_contact.phone) {
        const normalizedPhone = _contact.phone.replace(/^(\+1|1)/, "");
        var regPhone = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
        if (!regPhone.test(normalizedPhone)) {
          formIsValid = false;
          errors["phone"] = t("errors.InvalidPhoneNumber");
        }
      }

      if (_contact.email) {
        let regEmail =
          /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!regEmail.test(_contact.email)) {
          formIsValid = false;
          errors["email"] = "errors.InvalidEmailAddress";
        }
      }

      setFormErrors(errors);

      if (!formIsValid) {
        return;
      }

      // default name to user's local timestamp
      onSave({
        ..._contact,
        phone: _contact.phone
          ? _contact.phone.replace(/^(\+1|1)/, "")
          : _contact.phone,
        name: _contact.name || `Untitled ${new Date().toLocaleString()}`,
      });
    },
    [_contact, onSave]
  );

  const _onCancel = useCallback(
    (e) => {
      e.preventDefault();
      onCancel();
    },
    [onCancel]
  );

  const _onArchive = useCallback(
    (e) => {
      e.preventDefault();
      setConfirmingArchive(true);
    },
    [onArchive]
  );

  const hasTypeSelected = _contact.type > 0;

  return (
    <form className={styles.form} onSubmit={_onSubmit}>
      <div className={styles.fields}>
        <div className={styles.inputs}>
          <h4 className={styles.header}>
            {t("labels.contactEditContactInfo")}
          </h4>

          <TextInput
            value={_contact.name}
            placeholder={t("labels.contactName")}
            onChange={(e) => void _mergeContact({ name: e.target.value })}
          />
          {formErrors && formErrors.name && (
            <div>
              <span className={styles.missingValue}>{t(formErrors.name)}</span>
            </div>
          )}
          <TextInput
            value={_contact.phone}
            placeholder={t("labels.contactPhone")}
            onChange={(e) => void _mergeContact({ phone: e.target.value })}
            // pattern="[A-Za-z]{3}"
          />
          {formErrors && formErrors.phone && (
            <div>
              <span className={styles.missingValue}>{t(formErrors.phone)}</span>
            </div>
          )}
          <TextInput
            value={_contact.email}
            placeholder={t("labels.contactEmail")}
            onChange={(e) => void _mergeContact({ email: e.target.value })}
          />
          {formErrors && formErrors.email && (
            <div>
              <span className={styles.missingValue}>{t(formErrors.email)}</span>
            </div>
          )}

          <div className={styles.contactTypes}>
            <h4 className={styles.header}>
              {t("labels.contactEditType")}{" "}
              {!hasTypeSelected && (
                <span className={styles.missingValue}>
                  {t("labels.contactEditNoTypeSelected")}
                </span>
              )}
            </h4>

            <div className={styles.checkboxGroup}>
              <Checkbox
                id="typeCustomer"
                checked={hasBitwise(CONTACT_TYPE.Customer, _contact.type)}
                onChange={(e) => {
                  if (hasBitwise(CONTACT_TYPE.Customer, _contact.type)) {
                    _mergeContact({
                      type: _contact.type - CONTACT_TYPE.Customer,
                    });
                  } else {
                    _mergeContact({
                      type: _contact.type + CONTACT_TYPE.Customer,
                    });
                  }
                }}
              />
              <label htmlFor="typeCustomer">
                {t("labels.contactTypeCustomer")}
              </label>
            </div>
            <div className={styles.checkboxGroup}>
              <Checkbox
                id="typeProspect"
                checked={hasBitwise(CONTACT_TYPE.Prospect, _contact.type)}
                onChange={(e) => {
                  if (hasBitwise(CONTACT_TYPE.Prospect, _contact.type)) {
                    _mergeContact({
                      type: _contact.type - CONTACT_TYPE.Prospect,
                    });
                  } else {
                    _mergeContact({
                      type: _contact.type + CONTACT_TYPE.Prospect,
                    });
                  }
                }}
              />
              <label htmlFor="typeProspect">
                {t("labels.contactTypeProspect")}
              </label>
            </div>
          </div>
        </div>

        {mobile.isApp() && (
          <div className={styles.buttons}>
            <Button onClick={_onFind} icon={<IconContact />}>
              {t("buttons.find")}
            </Button>
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <div className={styles.actionRow}>
          <Button
            className={styles.actionButton}
            onClick={_onCancel}
            buttonStyle="hollowAccent"
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            className={styles.actionButton}
            onClick={_onSave}
            disabled={!hasTypeSelected}
          >
            {_contact.id == null
              ? t("buttons.createContact")
              : t("buttons.save")}
          </Button>
        </div>

        {_contact.id != null && (
          <div className={styles.actionRow}>
            <Button
              className={classNames(styles.actionButton, styles.archiveButton)}
              onClick={(e) => {
                e.preventDefault();
                setConfirmingArchive(true);
              }}
              buttonStyle="hollowBronze"
            >
              {t("buttons.archive")}
            </Button>
          </div>
        )}
      </div>

      <Modal isOpen={confirmingArchive}>
        <ModalContent>
          <p>{t("labels.contactEditArchiveConfirm")}</p>
        </ModalContent>
        <ModalControls>
          <Button
            buttonStyle="hollow"
            onClick={(e) => {
              setConfirmingArchive(false);
            }}
          >
            {t("buttons.cancel")}
          </Button>

          <Button
            buttonStyle="default"
            onClick={(e) => {
              setConfirmingArchive(false);
              onArchive();
            }}
          >
            {t("buttons.archive")}
          </Button>
        </ModalControls>
      </Modal>
    </form>
  );
}

ContactEdit.propTypes = {
  /**
   * Contact data to edit. onSave is called with an updated version of this object.
   */
  contact: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,

  /**
   * Called when the user clicks the Cancel button.
   */
  onCancel: PropTypes.func.isRequired,

  /**
   * Called when the user clicks the Save button. Receives an updated contact object.
   */
  onSave: PropTypes.func.isRequired,

  /**
   * Called when the user clicks the Archive button.
   */
  onArchive: PropTypes.func.isRequired,
};
